import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import type { Organization } from '../../../core/models/Organization';
import { OrganizationStatus } from '../../../core/models/Organization';

interface OrganizationDialogProps {
  open: boolean;
  onClose: () => void;
  organization?: Partial<Organization>;
  onSubmit: (data: any) => Promise<void>;
}

export const OrganizationDialog: React.FC<OrganizationDialogProps> = ({
  open,
  onClose,
  organization,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      name: organization?.name || '',
      status: organization?.status || OrganizationStatus.ACTIVE,
      address: {
        street: organization?.address?.street || '',
        city: organization?.address?.city || '',
        state: organization?.address?.state || '',
        postalCode: organization?.address?.postalCode || '',
        country: organization?.address?.country || 'USA',
      },
    },
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{organization ? 'Edit Client' : 'Create Client'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Client Name"
            {...register('name', { required: 'Name is required' })}
            error={errors.name?.message}
          />
          <div className="space-y-2">
            <label className="text-sm font-medium">Status</label>
            <Select
              value={watch('status')}
              onValueChange={(value) => setValue('status', value as OrganizationStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={OrganizationStatus.ACTIVE}>Active</SelectItem>
                <SelectItem value={OrganizationStatus.INACTIVE}>Inactive</SelectItem>
                <SelectItem value={OrganizationStatus.SUSPENDED}>Suspended</SelectItem>
                <SelectItem value={OrganizationStatus.PENDING}>Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-4">
            <h3 className="font-semibold">Address</h3>
            <Input label="Street" {...register('address.street')} />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Input label="City" {...register('address.city')} />
              <Input label="State" {...register('address.state')} />
              <Input label="Postal Code" {...register('address.postalCode')} />
            </div>
            <Input label="Country" {...register('address.country')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {organization ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
